// Card #1637 — the scoreboard's shared venue-local time formatter (design
// docs/score/scoreboard-design.md §4.2/§8.4). This is meant to be the LAST
// venue-local time formatter anybody writes for the scoreboard surface:
// every later scoreboard card renders times through this, not a bespoke
// Intl call of its own.
//
// #1788 — this used to export a hardcoded
// `VENUE_TIMEZONE = "America/Los_Angeles"`, on the stated grounds that no
// score-show.timeZone field existed. That stopped being true: WEC (Ocala,
// Florida) is Eastern, so every time the board rendered for it was 3 hours
// early. The constant is now a DEFAULT; the real zone rides down from the
// API on the scoreboard poll response's `venueTimezone` key and is passed
// in explicitly by callers.
//
// This is a deliberate DUPLICATE of apps/score-api/src/lib/venue-time.js's
// resolver, matching how the two constants were already duplicated: the FE
// bundle must not import score-api, and both sides have to agree on what a
// stored offset means. The two are kept in sync by identical unit tests
// pinning the same WEC instant to the same clock face on both sides.
export const DEFAULT_VENUE_TIMEZONE = "America/Los_Angeles";

// 🔴 #1811 — the zone FVD STAMPS ARE MINTED IN. This is NOT the venue's zone,
// it only happens to share a value with the Pacific default above, and the two
// must never be collapsed back into one constant.
//
// `scheduledTime` is not a genuine UTC instant. FVD's Access DB holds a naive
// venue wall-clock time; ADODB on `fvdts` parses that naive value as
// BOX-local, and `fvdts` is set to America/Los_Angeles; foxbox then reads it
// back with UTC getters. So WEC's 8:08 AM EASTERN ride is stored as
// 2026-08-21T15:08:00.000Z — which is 11:08 AM Eastern, three hours off the
// truth. (Measured on dev, 2026-08-21, show fvd:show:showKey-oes:e83e7ef8….)
//
// Rendering that stamp back through Pacific is the EXACT INVERSE of the
// mangling, which is the only reason class times have ever displayed
// correctly. The Pacific here is load-bearing, not a leftover from before
// `Show.venueTimezone` existed.
//
// So: anything sourced from FVD (`scheduledTime`, and everything derived from
// it — `classStart`, `estimatedFinish`, `rideTime`) renders through THIS
// constant, whatever venue the show is at. Genuine instants (`serverTime`,
// `lastUpdated`, the live clock) render through the show's `venueTimezone`.
// Point a stamp at the venue zone and every ride time jumps by the offset
// between `fvdts` and the venue — three hours late at WEC.
//
// This disappears only when ingest is fixed to stamp genuine UTC and the
// existing rows are migrated (card #1814). Until then, deleting this is a
// three-hour production bug that looks like a cleanup — which is exactly what
// card #1805 proposed before it was measured.
export const FVD_STAMP_TIMEZONE = "America/Los_Angeles";

// Shows are configured with a GMT-n OFFSET, not an IANA zone name (Jaybill,
// 2026-08-20). `Intl` rejects a bare "GMT-4", but the POSIX-style
// `Etc/GMT±N` zones mean exactly "this fixed offset, no DST", so mapping
// onto one lets every existing Intl call keep working with only the zone
// STRING differing.
//
// 🔴 The sign is INVERTED and that is correct: POSIX `Etc/GMT+4` is four
// hours BEHIND UTC (what a human calls "GMT-4"). Backwards is an 8-hour
// error, not a no-op, which is why the tests pin a real instant to a real
// clock face rather than asserting on the mapping.
const OFFSET_PATTERN = /^(?:GMT|UTC)?\s*([+-])\s*(\d{1,2})(?::?(\d{2}))?$/i;

const MIN_OFFSET_HOURS = -14;
const MAX_OFFSET_HOURS = 12;

const zoneValidityCache = new Map();

function isZoneUsable(timeZone) {
  if (zoneValidityCache.has(timeZone)) return zoneValidityCache.get(timeZone);
  let usable = true;
  try {
    new Intl.DateTimeFormat("en-CA", { timeZone });
  } catch {
    usable = false;
  }
  zoneValidityCache.set(timeZone, usable);
  return usable;
}

// #2097 — the zone a configured value resolves to, or null when the value
// is absent, malformed, out of range, not a whole hour, or not a zone Intl
// knows. null is exactly the set of inputs resolveVenueTimeZone below answers
// with the Pacific DEFAULT, so "would this silently fall back?" has ONE
// definition shared by the resolver and by isVenueTimezoneConfigured.
function resolveConfiguredZone(configured) {
  if (typeof configured !== "string") return null;
  const trimmed = configured.trim();
  if (!trimmed) return null;

  if (/^(?:GMT|UTC)$/i.test(trimmed)) return "Etc/GMT";

  const match = OFFSET_PATTERN.exec(trimmed);
  if (match) {
    const [, sign, hoursRaw, minutesRaw] = match;
    if (minutesRaw && Number(minutesRaw) !== 0) return null;
    const hours = Number(hoursRaw) * (sign === "-" ? -1 : 1);
    if (hours < MIN_OFFSET_HOURS || hours > MAX_OFFSET_HOURS) return null;
    if (hours === 0) return "Etc/GMT";
    return `Etc/GMT${hours < 0 ? "+" : "-"}${Math.abs(hours)}`;
  }

  return isZoneUsable(trimmed) ? trimmed : null;
}

/**
 * Turn a show's configured venue timezone into a string `Intl` accepts.
 * Accepts "GMT-4"/"UTC-4"/"-4"/"-04:00" or an IANA name. Anything absent,
 * malformed, out of range, or not a whole hour resolves to the Pacific
 * default — this NEVER throws (#1798: the board never shows an error).
 *
 * 🔴 #2097 — that default is SILENT. A caller that renders a time to a human
 * must not render the result of this for an unconfigured show without a
 * visible signal: gate on isVenueTimezoneConfigured and render the unset
 * state, or use formatVenueTimeOrUnset / formatVenueDateTimeOrUnset below,
 * which do both.
 */
export function resolveVenueTimeZone(configured) {
  return resolveConfiguredZone(configured) ?? DEFAULT_VENUE_TIMEZONE;
}

/**
 * #2097 — true only when `configured` names a zone the resolver can honour
 * WITHOUT falling back to the Pacific default (absent, blank, non-string,
 * malformed and unknown-zone values are all false). A persisted show object
 * cached before the field existed is just "absent" here, so it surfaces the
 * unset state instead of a plausible Pacific time.
 */
export function isVenueTimezoneConfigured(configured) {
  return resolveConfiguredZone(configured) !== null;
}

// #2097 — the one English fallback for the unset state. Callers with i18n
// pass their own translated `unsetText`; this exists so a caller that forgets
// still renders an explicit unconfigured label, never a Pacific time.
export const VENUE_TIME_UNSET_TEXT = "Venue timezone not set";

// Accepts a Date, an ISO string, or an epoch number; returns null for a
// value that fails to parse — a scoreboard must never throw while rendering
// a malformed timestamp, it should just render a blank time.
//
// #1820 — STRINGS carry the same two input-shape guards the other venue-day
// copies already have (report-defs class-day.js #689, scribe-fe classDisplay.js
// #1559, announcer-fe formatTime.js #1649); this was the last bare
// `new Date(value)`. It backs venueDayKey/fvdStampDayKey for the whole
// score-fe scoreboard family (#1637/#1788/#1811), so without them:
//   1. a date-ONLY string ("2026-08-04") parses as UTC MIDNIGHT and reads
//      back as the PREVIOUS venue day in any zone west of UTC. There is no
//      way to recover the intended day from it, so it is null (the board
//      renders a blank / drops the key, it never invents a wrong day).
//   2. an UNZONED datetime string ("2026-08-04T05:00:00") parses as
//      DEVICE-local per ECMA-262, so the key would depend on the kiosk's own
//      timezone. Stamps and instants always MEAN UTC, so append a "Z".
// Date instances and epoch numbers are genuine instants and pass through
// untouched; null-and-drop (never throw) because the scoreboard is a kiosk.
function toDate(value) {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (value == null) return null;
  let input = value;
  if (typeof value === "string") {
    const raw = value.trim();
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw)) return null;
    input = /[Zz]|[+-]\d{2}:?\d{2}$/.test(raw) ? raw : `${raw}Z`;
  }
  const d = new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

// "8:10 AM" (or "8:10:05 AM" with `seconds: true`), venue-local — never UTC,
// never the browser's own zone. `timeZone` accepts a configured offset or an
// IANA name; omitting it falls back to Pacific. Returns "" for an
// unparseable/absent value so a caller can render it directly without an
// extra guard.
//
// #1889 — `hour12` defaults to true, which is exactly the en-US behavior
// every pre-existing caller already got (hour: "numeric" renders 12-hour
// with AM/PM), so passing nothing changes nothing. hour12: false switches to
// a zero-padded 24-hour face ("15:47:23") via hourCycle "h23" — "h23", not
// en-US's bare hour12: false, because some ICU builds render midnight as
// "24:00" under the locale default. Added for scribe-fe's venue clock, which
// honors the scribe's stored 12h/24h display preference (#710) — the SAME
// formatter must serve both faces so the clock can never disagree with the
// scheduled-time labels beside it.
export function formatVenueTime(
  value,
  { timeZone = DEFAULT_VENUE_TIMEZONE, seconds = false, hour12 = true } = {},
) {
  const d = toDate(value);
  if (!d) return "";
  return d.toLocaleTimeString("en-US", {
    ...(hour12
      ? { hour: "numeric", hour12: true }
      : { hour: "2-digit", hourCycle: "h23" }),
    minute: "2-digit",
    ...(seconds ? { second: "2-digit" } : {}),
    timeZone: resolveVenueTimeZone(timeZone),
  });
}

// #1889 AC6 — the short human label for the zone a configured venueTimezone
// RESOLVES to ("EDT", "PST", "GMT-5"), for rendering next to a venue clock
// so a wrong or defaulted zone is obvious at a glance instead of only when
// someone thinks to check. Derived from the resolved zone via Intl (the same
// resolveVenueTimeZone every formatter goes through), never from the raw
// configured string, so an offset-style config ("GMT-4") labels itself the
// same way an IANA name does. Returns the resolved zone string itself if Intl
// yields no zoneName part (cannot happen on any engine that supports
// timeZoneName, but a label renderer must not throw).
export function venueZoneLabel(configured, now = new Date()) {
  const zone = resolveVenueTimeZone(configured);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    timeZoneName: "short",
  }).formatToParts(now);
  return parts.find((p) => p.type === "timeZoneName")?.value ?? zone;
}

// The venue-local calendar day ("2026-08-09") a timestamp falls on. en-CA's
// default date format happens to BE "YYYY-MM-DD", a dependency-free way to
// get a sortable/comparable key out of Intl instead of hand-rolling one.
// Deliberately NOT a string-slice of the ISO value: a timestamp stamped late
// evening venue-local can already be the next day in UTC, and this must
// still resolve to the venue day a human at the show would name.
export function venueDayKey(value, timeZone = DEFAULT_VENUE_TIMEZONE) {
  const d = toDate(value);
  if (!d) return null;
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: resolveVenueTimeZone(timeZone),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

// #2097 — the loud counterpart of formatVenueTime for a GENUINE instant that
// belongs on the show's own venueTimezone. When the zone is not configured
// (see isVenueTimezoneConfigured) it returns the explicit unset text instead
// of a clean, plausible, wrong Pacific time. An unparseable VALUE still
// returns "" (nothing to show), checked first so a blank cell stays blank.
export function formatVenueTimeOrUnset(
  value,
  { timeZone, unsetText = VENUE_TIME_UNSET_TEXT, ...rest } = {},
) {
  if (!toDate(value)) return "";
  if (!isVenueTimezoneConfigured(timeZone)) return unsetText;
  return formatVenueTime(value, { ...rest, timeZone });
}

// #2097 — date + time for audit-trail style stamps (e.g. "Aug 21, 2026, 8:08
// AM EDT"). `options` are Intl.DateTimeFormat options. The RESOLVED zone's
// short label is printed beside the time unless `options` already asks Intl
// for a timeZoneName (the #1889 precedent: a wrong zone is obvious at a
// glance). The label is appended rather than passed as `timeZoneName` so the
// dateStyle/timeStyle option shapes, which Intl refuses to mix with
// timeZoneName, keep working. Unset zone -> `unsetText`; unparseable value
// -> "".
export function formatVenueDateTimeOrUnset(
  value,
  {
    timeZone,
    unsetText = VENUE_TIME_UNSET_TEXT,
    options = { dateStyle: "medium", timeStyle: "short" },
  } = {},
) {
  const d = toDate(value);
  if (!d) return "";
  if (!isVenueTimezoneConfigured(timeZone)) return unsetText;
  const text = d.toLocaleString("en-US", {
    ...options,
    timeZone: resolveVenueTimeZone(timeZone),
  });
  return options.timeZoneName ? text : `${text} ${venueZoneLabel(timeZone, d)}`;
}

// #1811 — render an FVD-sourced timestamp (`scheduledTime`, `rideTime`,
// `classStart`, `estimatedFinish`). Deliberately takes NO `timeZone` option:
// there is exactly one right answer (see FVD_STAMP_TIMEZONE above), and a
// caller that can pass a zone is a caller that will eventually be handed the
// venue's. The separate name is the guardrail — `formatVenueTime(stamp, {
// timeZone })` and `formatFvdStampTime(stamp)` look different at the call
// site, which is the whole point.
export function formatFvdStampTime(value, { seconds = false, zone } = {}) {
  return formatVenueTime(value, { timeZone: stampZoneOf(zone), seconds });
}

// #1814 AC3 -- the zone an FVD stamp must be read in. foxbox stamps a ride in
// a configured venue zone as a GENUINE UTC instant and says so with
// `scheduledTimeZone` (an IANA id) on the row; such a stamp is read in that
// zone. A row with no (or an unusable) marker is the legacy Pacific-as-UTC
// stamp and reads through FVD_STAMP_TIMEZONE exactly as before, so no deploy
// or config order can invert a time. Pass the marker from the SAME row the
// stamp came from; it is not the show's venueTimezone.
export function stampZoneOf(scheduledTimeZone) {
  const marked =
    typeof scheduledTimeZone === "string" ? resolveConfiguredZone(scheduledTimeZone.trim()) : null;
  return marked ?? FVD_STAMP_TIMEZONE;
}

// The calendar day an FVD stamp falls on, in the zone it was minted in (its
// row's marker, else the legacy stamp zone). Same reasoning as
// formatFvdStampTime: a stamp's day must be recovered through the zone it was
// minted in, not the venue's.
export function fvdStampDayKey(value, zone) {
  return venueDayKey(value, stampZoneOf(zone));
}

export default formatVenueTime;
